package com.siriusxm.pia.views.unifiedaggregator.backfill

import com.siriusxm.cmc.utils.arn
import com.siriusxm.pia.CapabilityAction
import com.siriusxm.pia.CapabilityQuery
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put

/**
 * AVP action the catalog API requires for every human backfill operation: start (`POST /backfill`),
 * cancel (`DELETE /backfill/{id}`) and test (`POST /backfill/test`). One grant covers all three.
 */
internal val BackfillCapability = CapabilityAction("UnifiedContentAggregator_Backfill")

/**
 * AVP action the catalog API requires for every human consumer replay operation: start
 * (`POST /replay`) and cancel (`DELETE /replay/{id}`). One grant covers both.
 */
internal val ReplayCapability = CapabilityAction("UnifiedContentAggregator_Replay")

/**
 * The canonical destination kind for a target ARN, matching the catalog API's `targetType` context
 * value (`TargetOperationContext.targetType`). Null when the ARN cannot be parsed, in which case
 * the key is left out of the context rather than guessed.
 */
internal fun targetTypeFor(targetArn: String): String? =
    when (val service = targetArn.arn()?.service) {
        null -> null
        "kinesis" -> "KINESIS"
        "events" -> "EVENTBRIDGE"
        "lambda" -> "LAMBDA"
        "sqs" -> "SQS"
        "logs" -> "LOG_GROUP"
        else -> service.uppercase()
    }

/**
 * The Cedar context for a per-target probe: `targetArn`, plus `consumerId`, `targetAccountId` and
 * `targetType` when they are known. This deliberately omits `entityTypes` (the user has not chosen
 * any yet), so the server's policies must guard that key with `context has entityTypes`. A probe
 * answers "may this user run this operation against this target at all"; the server still checks
 * every selected entity type on the request itself.
 */
internal fun targetProbeContext(targetArn: String, consumerId: String? = null): JsonObject =
    buildJsonObject {
        put("targetArn", targetArn)
        consumerId?.let { put("consumerId", it) }
        targetArn.arn()?.accountId?.takeIf { it.isNotBlank() }?.let { put("targetAccountId", it) }
        targetTypeFor(targetArn)?.let { put("targetType", it) }
    }

/** The "may I run backfill operations against this target" question, hide-only. */
internal fun backfillProbe(targetArn: String, consumerId: String? = null): CapabilityQuery =
    CapabilityQuery(BackfillCapability, context = targetProbeContext(targetArn, consumerId))

/** The "may I run replay operations against this target" question, hide-only. */
internal fun replayProbe(targetArn: String, consumerId: String? = null): CapabilityQuery =
    CapabilityQuery(ReplayCapability, context = targetProbeContext(targetArn, consumerId))
